import React, { lazy } from 'react';
import RouteContent from './components/common/RouteContent';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { ThemeProvider } from '@mui/material/styles';
import HomePage from './components/HomePage';
import { AuthGuard } from './components/AuthGuard';
import CompetitionAccessGate from './components/CompetitionAccessGate';
import ScrollRestoration from './components/ScrollRestoration';
import VipTeaser from './components/VipTeaser';
import { darkTheme } from './components/theme';
import { CompetitionProvider } from './components/CompetitionContext';
import AppLayout from './layouts/AppLayout';
import { AnalyticsProvider } from './contexts/AnalyticsContext';
import EntityUrlRedirect from './components/common/EntityUrlRedirect';
import PageStatus from './components/common/PageStatus';

const LeaguePage = lazy(() => import('./components/LeaguePage'));
const TeamComparisons = lazy(() => import('./components/TeamComparisons'));
const Summary = lazy(() => import('./components/Summary'));
const EloRanking = lazy(() => import('./components/EloRanking'));
const PositionMatrix = lazy(() => import('./components/PositionMatrix'));
const WcPositionMatrix = lazy(() => import('./components/WcPositionMatrix'));
const WcSummary = lazy(() => import('./components/WcSummary'));
const WcFixtures = lazy(() => import('./components/WcFixtures'));
const WcCompetitionEloRanking = lazy(() => import('./components/WcCompetitionEloRanking'));
const WcLeaguePage = lazy(() => import('./components/WcLeaguePage'));
const WcPointThreshold = lazy(() => import('./components/WcPointThreshold'));
const WcYourSimulation = lazy(() => import('./components/WcYourSimulation'));
const WcStandingsCalculator = lazy(() => import('./components/WcStandingsCalculator'));
const WcMatchesProbabilities = lazy(() => import('./components/WcMatchesProbabilities'));
const WcBookmakerOddsComparison = lazy(() => import('./components/WcBookmakerOddsComparison'));
const BlogList = lazy(() => import('./components/BlogList'));
const ArticlePage = lazy(() => import('./components/ArticlePage'));
const LeagueTables = lazy(() => import('./components/LeagueTables'));
const SimulationResults = lazy(() => import('./components/SimulationResults'));
const Fixtures = lazy(() => import('./components/Fixtures'));
const YourSimulation = lazy(() => import('./components/yourSimulation'));
const Login = lazy(() => import('./components/Login'));
const Register = lazy(() => import('./components/Register'));
const AdminPanel = lazy(() => import('./components/AdminPanel'));
const AddMatch = lazy(() => import('./components/AddMatch'));
const ManageSimulations = lazy(() => import('./components/ManageSimulations'));
const CreatePost = lazy(() => import('./components/CreatePost'));
const PointsThreshold = lazy(() => import('./components/PointsThreshold'));
const TeamPage = lazy(() => import('./components/TeamPage'));
const FixtureDifficulty = lazy(() => import('./components/FixtureDifficulty'));
const CompetitionEloRanking = lazy(() => import('./components/CompetitionEloRanking'));
const AchievementRanking = lazy(() => import('./components/achievements/AchievementRanking'));
const AchievementList = lazy(() => import('./components/achievements/AchievementList'));
const TeamAnalytics = lazy(() => import('./components/TeamAnalytics'));
const LeagueManager = lazy(() => import('./components/AdminPanel/LeagueManager'));
const SeasonImportManager = lazy(() => import('./components/AdminPanel/SeasonImportManager'));
const FriendliesManager = lazy(() => import('./components/AdminPanel/FriendliesManager'));
const AppSettingsManager = lazy(() => import('./components/AdminPanel/AppSettingsManager'));
const TeamsWithoutMatches = lazy(() => import('./components/AdminPanel/TeamsWithoutMatches'));
const LeaguesWithoutUpcomingMatches = lazy(() => import('./components/AdminPanel/LeaguesWithoutUpcomingMatches'));
const PastMatchesWithoutResults = lazy(() => import('./components/AdminPanel/PastMatchesWithoutResults'));
const EloAnomalies = lazy(() => import('./components/AdminPanel/EloAnomalies'));
const EuropeDrawsManager = lazy(() => import('./components/AdminPanel/EuropeDrawsManager'));
const SocialPlanManager = lazy(() => import('./components/AdminPanel/SocialPlanManager'));
const StandingsCalculator = lazy(() => import('./components/StandingsCalculator'));
const Playoff = lazy(() => import('./components/Playoff'));
const LiveSimulation = lazy(() => import('./components/LiveSimulation'));
const PstrykPage = lazy(() => import('./components/PstrykPage'));
const ScenarioLab = lazy(() => import('./components/ScenarioLab'));
const BillingCheckoutPage = lazy(() => import('./components/BillingCheckoutPage'));
const BillingResultPage = lazy(() => import('./components/BillingResultPage'));
const SubscriptionPage = lazy(() => import('./components/SubscriptionPage'));
const LegalPage = lazy(() => import('./components/LegalPage'));

function App() {
  return (
    <ThemeProvider theme={darkTheme}>
      <CompetitionProvider>
        <BrowserRouter>
          <AnalyticsProvider>
          <AppLayout>
            <ScrollRestoration />
            <RouteContent>
            <EntityUrlRedirect><Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<Login />} />
              <Route path="/register" element={<Register />} />
              <Route path="/vip" element={<VipTeaser />} />
              <Route path="/kup-vip" element={<BillingCheckoutPage />} />
              <Route path="/billing/success" element={<BillingResultPage status="success" />} />
              <Route path="/billing/cancel" element={<BillingResultPage status="cancel" />} />
              <Route path="/konto/subskrypcja" element={<SubscriptionPage />} />
              <Route path="/dokumenty/:slug" element={<LegalPage />} />
              <Route path="/dokumenty" element={<LegalPage />} />
              <Route path="/liga/431/wcPositionMatrix" element={<WcPositionMatrix />} />
              <Route path="/liga/431/wcYourSimulation" element={<CompetitionAccessGate competitionIdOverride={431} requirementField="full_requirement"><WcYourSimulation /></CompetitionAccessGate>} />
              <Route path="/liga/431/wcSummary" element={<WcSummary />} />
              <Route path="/liga/431/wcPointThreshold" element={<WcPointThreshold />} />
              <Route path="/liga/431/WcStandingsCalculator" element={<WcStandingsCalculator />} />
              <Route path="/liga/431/elo-ranking" element={<WcCompetitionEloRanking />} />
              <Route path="/liga/431/fixtures" element={<WcFixtures />} />
              <Route path="/liga/431/wcMatchesProbabilities" element={<WcMatchesProbabilities />} />
              <Route path="/liga/:competitionId/conditions" element={<ScenarioLab />} />
              <Route path="/liga/431/:slug?" element={<WcLeaguePage />} />
              <Route path="/liga/:id/:slug?" element={<LeaguePage />} />
              <Route path="/liga/:competitionId/positionMatrix" element={<CompetitionAccessGate requirementField="stats_requirement"><PositionMatrix /></CompetitionAccessGate>} />
              <Route path="/liga/:competitionId/playoff" element={<CompetitionAccessGate requirementField="full_requirement"><Playoff /></CompetitionAccessGate>} />
              <Route path="/liga/:competitionId/team-comparisons" element={<CompetitionAccessGate requirementField="stats_requirement"><TeamComparisons /></CompetitionAccessGate>} />
              <Route path="/liga/:competitionId/summary" element={<CompetitionAccessGate requirementField="stats_requirement"><Summary /></CompetitionAccessGate>} />
              <Route path="/liga/:competitionId/fixtures" element={<Fixtures />} />
              <Route path="/liga/:competitionId/points-threshold" element={<CompetitionAccessGate requirementField="stats_requirement"><PointsThreshold /></CompetitionAccessGate>} />
              <Route path="/liga/:id/standingsCalculator" element={<StandingsCalculator />} />
              <Route path="/liga/:competitionId/fixture_difficulty" element={<CompetitionAccessGate requirementField="full_requirement"><FixtureDifficulty /></CompetitionAccessGate>} />
              <Route path="/liga/:id/leagueTables" element={<LeagueTables />} />
              <Route path="/liga/:id/live" element={<AuthGuard requiredRole="admin"><LiveSimulation /></AuthGuard>} />
              <Route path="/liga/:id/yourSimulation" element={<YourSimulation />} />
              <Route path="/liga/:competitionId/elo-ranking" element={<CompetitionAccessGate requirementField="stats_requirement"><CompetitionEloRanking /></CompetitionAccessGate>} />
              <Route path="/achievements/:category" element={<AchievementRanking />} />
              <Route path="/achievements-list" element={<AchievementList />} />
              <Route path="/team/:id/analytics" element={<TeamAnalytics />} />
              <Route path="/odds" element={<WcBookmakerOddsComparison />} />
              <Route path="/team/:teamId/:slug?" element={<TeamPage />} />
              <Route path="/elo-ranking" element={<EloRanking />} />
              <Route path="/blog" element={<BlogList />} />
              <Route path="/blog/:slug" element={<ArticlePage />} />
              <Route path="/simulation-results" element={<SimulationResults />} />
              <Route path="/pstryk" element={<PstrykPage />} />
              <Route path="/janekjot" element={<AuthGuard requiredRole="admin"><AdminPanel /></AuthGuard>} />
              <Route path="/janekjot/addMatch" element={<AuthGuard requiredRole="admin"><AddMatch /></AuthGuard>} />
              <Route path="/janekjot/manageSimulations" element={<AuthGuard requiredRole="admin"><ManageSimulations /></AuthGuard>} />
              <Route path="/janekjot/leagueManager" element={<AuthGuard requiredRole="admin"><LeagueManager /></AuthGuard>} />
              <Route path="/janekjot/seasonImport" element={<AuthGuard requiredRole="admin"><SeasonImportManager /></AuthGuard>} />
              <Route path="/janekjot/friendlies" element={<AuthGuard requiredRole="admin"><FriendliesManager /></AuthGuard>} />
              <Route path="/janekjot/settings" element={<AuthGuard requiredRole="admin"><AppSettingsManager /></AuthGuard>} />
              <Route path="/janekjot/druzyny-bez-meczow" element={<AuthGuard requiredRole="admin"><TeamsWithoutMatches /></AuthGuard>} />
              <Route path="/janekjot/ligi-bez-najblizszych-meczow" element={<AuthGuard requiredRole="admin"><LeaguesWithoutUpcomingMatches /></AuthGuard>} />
              <Route path="/janekjot/mecze-przeszle-bez-wyniku" element={<AuthGuard requiredRole="admin"><PastMatchesWithoutResults /></AuthGuard>} />
              <Route path="/janekjot/bledne-zmiany-elo" element={<AuthGuard requiredRole="admin"><EloAnomalies /></AuthGuard>} />
              <Route path="/janekjot/europeDraws" element={<AuthGuard requiredRole="admin"><EuropeDrawsManager /></AuthGuard>} />
              <Route path="/janekjot/social-plan" element={<AuthGuard requiredRole="admin"><SocialPlanManager /></AuthGuard>} />
              <Route path="/janekjot/createPost" element={<AuthGuard requiredRole="admin"><CreatePost /></AuthGuard>} />
              <Route path="/janekjot/odds" element={<WcBookmakerOddsComparison />} />
              <Route path="*" element={<PageStatus />} />
            </Routes></EntityUrlRedirect>
            </RouteContent>
          </AppLayout>
          </AnalyticsProvider>
        </BrowserRouter>
      </CompetitionProvider>
    </ThemeProvider>
  );
}

export default App;
